import { type Maybe } from '@passionware/monads';
import { type ReactNode, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import type { WorkspaceScoping } from 'v5/api/_common/query/workspace-scoping';
import { Button } from 'v5/design-system/Button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from 'v5/design-system/Dialog';
import type { WithServices } from 'v5/platform/ts/services';
import type { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import type { WithEnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';
import {
  ScopeBadge,
  type ScopeBadgeProps
} from 'v5/patterns/page-layout/ScopeBadge';
import { ResourcePickerCloseContext } from './ResourcePickerCommit';

type ScopeServices = ScopeBadgeProps['services'];

/**
 * A resource picker in a dialog: `Header`, then `Body` (the picker's `Main` and
 * `ResourcePicker.Inspector` side by side) and `Footer`. The children mount with the content, so
 * a closed dialog forgets its picks. `ResourcePicker.Commit` inside closes it once it has saved;
 * `Cancel` and the corner's close leave without saving.
 */
function ResourcePickerDialogRoot({
  open,
  onOpenChange,
  children
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[80vh] min-h-144 max-w-360 flex-col p-0">
        <ResourcePickerCloseContext.Provider value={() => onOpenChange(false)}>
          {children}
        </ResourcePickerCloseContext.Provider>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The title row: the title, the host's badges for what the pick is for (the slot, the group, the
 * contact), then whose resources are searched, from the scope the results read.
 */
function ResourcePickerDialogHeader({
  title,
  scope,
  services,
  children
}: {
  title: ReactNode;
  scope: Maybe<WorkspaceScoping>;
  services: ScopeServices;
  children?: ReactNode;
}) {
  return (
    <DialogHeader>
      <DialogTitle>
        {title}
        {children}
        <ScopeBadge scope={scope} meaning="lists" services={services} />
      </DialogTitle>
    </DialogHeader>
  );
}

/** The results and the open one's details; on a narrow screen the details cover only this. */
function ResourcePickerDialogBody({ children }: { children: ReactNode }) {
  return <div className="relative flex min-h-0 flex-1">{children}</div>;
}

/** The search and its results, left of the details. */
function ResourcePickerDialogMain({ children }: { children: ReactNode }) {
  return <div className="flex min-w-0 flex-1 flex-col">{children}</div>;
}

/** The host's actions for the pick: Add, Assign, a status to assign with. */
function ResourcePickerDialogFooter({ children }: { children: ReactNode }) {
  return (
    <DialogFooter className="mt-auto items-center border-t border-border-default px-4 py-3">
      {children}
    </DialogFooter>
  );
}

/** Leaves without saving, the pick dropped with the dialog; nothing outside a dialog. */
function ResourcePickerDialogCancel() {
  const { t } = useTranslation('patterns', { keyPrefix: 'ResourcePicker' });
  const close = useContext(ResourcePickerCloseContext);
  if (!close) return null;
  return (
    <Button type="button" variant="secondary" size="sm" onClick={close}>
      {t('cancel')}
    </Button>
  );
}

export const ResourcePickerDialog = Object.assign(ResourcePickerDialogRoot, {
  Header: ResourcePickerDialogHeader,
  Body: ResourcePickerDialogBody,
  Main: ResourcePickerDialogMain,
  Footer: ResourcePickerDialogFooter,
  Cancel: ResourcePickerDialogCancel
});
